<script lang="ts">
	import { FileUploader, getTusUploadId, type UploadedFile } from "file-uploader"
	import { DataHandlerMedia } from "luxedo-data"
	import { LuxedoRPC, waitForUploadReady } from "luxedo-rpc"
	import { Toast } from "toaster"
	import { mediaProgressHandler } from "../MediaProgressHandler.svelte"

	interface Props {
		entryId?: number
		folderId?: number
		onComplete: () => void
		onStart: () => void
	}

	let { entryId, folderId, onComplete, onStart }: Props = $props()

	const processedMedia = new Set<number>()

	const ALLOWED_MIME_TYPES = [
		"image/png",
		"image/jpeg",
		"image/jpg",
		"image/webp",
		"video/mp4",
		"video/quicktime",
		// "video/webm", removed after testing - doesn't seem to work
		"image/gif",
		"audio/mpeg",
		"audio/mp3",
		"audio/wav",
	]

	/**
	 * Called once per finished file. The API's post-finish hook created (or targeted) the media row
	 * and is copying the bytes into it; ask the API by tus upload id which row that was and wait for
	 * the copy, so each file starts processing exactly once, on its own row, and never before its
	 * source object exists. Replaces the old "diff the media library for 15 s" guess, which could
	 * attribute one new id to several uploads or miss a slow one entirely.
	 */
	async function onUploadSuccess(file?: UploadedFile) {
		const tusId = getTusUploadId(file)
		if (!tusId) {
			Toast.error(`${file?.name ?? "A file"} uploaded but tusd returned no upload id, so it cannot be processed.`)
			console.error("Media upload finished without a tus upload id", file)
			return
		}

		let mediaId: number
		try {
			const ready = await waitForUploadReady(tusId, (id) => LuxedoRPC.api.upload.tus_upload_state(id), {
				sizeBytes: file?.size,
			})
			mediaId = ready.entry_id
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err)
			Toast.error(`${file?.name ?? "A file"}: ${message}`)
			console.error("Media upload could not be finalized", file?.name, err)
			return
		}

		if (entryId && mediaId !== entryId) {
			Toast.error(`The server finalized the upload into media ${mediaId}, not the media being replaced (${entryId}).`)
			console.error("Re-upload landed on an unexpected media row", { expected: entryId, got: mediaId })
			return
		}

		if (processedMedia.has(mediaId)) return

		// Uppy discards this promise, so a rejection here would reach nobody: the overlay has
		// already closed on a success toast and the tile would never enter processing, never
		// show Failed and never get a thumbnail. Say so instead, and only count the media as
		// dispatched once it actually was (PR #1052 review).
		try {
			await DataHandlerMedia.pull([mediaId])
			await LuxedoRPC.api.media.media_process_begin(mediaId)
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err)
			Toast.error(`${file?.name ?? "A file"} uploaded, but processing could not be started: ${message}`)
			console.error("Could not start media processing", file?.name, mediaId, err)
			// The overlay has closed on a success toast by now; the tile is what the user is left with.
			mediaProgressHandler.markDispatchFailed(mediaId)
			return
		}
		processedMedia.add(mediaId)
		mediaProgressHandler.setTemporaryProgress(mediaId, 0)
	}

	function handleSuccess() {
		Toast.success("Upload(s) complete! Processing now...")
		onComplete()
	}

	const DEFAULT_ERROR_MESSAGE = "Upload(s) failed... Please refresh and try again."
	let errorMessage: string = DEFAULT_ERROR_MESSAGE
	function handleFail() {
		Toast.error(errorMessage)
	}

	function handleMixed(successAmount: number, totalAmount: number) {
		Toast.text(`${successAmount} of ${totalAmount} uploaded successfully! Processing now.`)
		onComplete()
	}
</script>

<FileUploader
	endpoint={import.meta.env.VITE_UPLOAD_URL}
	allowedMimeTypes={ALLOWED_MIME_TYPES}
	maxFileLimit={entryId ? 1 : 15}
	uploadType={"media"}
	getUploadToken={async () =>
		(await LuxedoRPC.api.upload.tus_upload_token({ upload_type: "media", entry_id: entryId ?? null })).upload_token}
	meta={{
		folder_id: folderId,
		entry_id: entryId,
	}}
	onStart={() => {
		errorMessage = DEFAULT_ERROR_MESSAGE
	}}
	onUploadComplete={onUploadSuccess}
	onError={(e) => {
		console.log(e, e.code)
		if (e.code === 507) errorMessage = "Not enough storage."
		else if (e.code === 415) errorMessage = "Invalid file type."
		else errorMessage = "Unable to begin upload."
	}}
	onAllComplete={(e) => {
		if (e.failed.length && e.successful.length)
			return handleMixed(e.successful.length, e.successful.length + e.failed.length)
		else if (e.failed.length) return handleFail()
		else if (e.successful.length) return handleSuccess()
	}}
/>

<style>
</style>
